import KeenSlider from "keen-slider";
import event from "../lib/mixins/event";

// Shift+wheel drag-scroll tuning — see _onWheel()/_tick() below.
const WHEEL_IMPULSE = 0.6; // wheel-delta px -> velocity px/frame
const MAX_VELOCITY = 60; // px/frame, clamps very aggressive flicks
const FRICTION = 0.92; // per-frame velocity decay (glide-to-stop)
const MIN_VELOCITY = 0.05; // px/frame threshold to stop the rAF loop
const MAX_TILT_DEG = 30; // clamps the 3D tilt at high velocity
const TILT_VELOCITY_FACTOR = 0.7; // deg per px/frame of velocity
const TILT_SMOOTHING = 0.03; // 0-1, lower = smoother but laggier

class CardCarousel {
	constructor(dom) {
		this.NAME = "CardCarousel";
		this.dom = dom;
		this.slider = null;
		this.velocity = 0;
		this.rafId = null;
		this.slideInners = [];
		this.observeRafId = null;
		this.lastPosition = 0;
		this.smoothedVelocity = 0;
		this._onWheel = this._onWheel.bind(this);
		this._tick = this._tick.bind(this);
		this._observeTilt = this._observeTilt.bind(this);
	}

	init() {
		const sliderDom = this.dom.querySelector(".card-carousel__slider");
		const wrap = this.dom.closest(".card-carousel");
		const prevBtn = wrap ? wrap.querySelector("[data-nav-prev]") : null;
		const nextBtn = wrap ? wrap.querySelector("[data-nav-next]") : null;

		if (!sliderDom) return;

		this.slider = new KeenSlider(sliderDom, {
			loop: true,
			slides: { perView: 1.3, spacing: 16, origin: "center" },
			breakpoints: {
				"(min-width: 1024px)": {
					slides: { perView: 4.6, spacing: 16, origin: "center" },
				},
			},
			created: (s) => {
				this.slideInners = s.slides.map((slide) =>
					slide.querySelector(".card-carousel__slide-inner")
				);
				this.lastPosition = s.track.details.position;
				this.observeRafId = requestAnimationFrame(this._observeTilt);
			},
		});

		// Looping means there's no start/end to disable the buttons at —
		// both stay active for the life of the carousel.
		if (prevBtn) {
			prevBtn.style.opacity = "";
			prevBtn.style.pointerEvents = "";
		}
		if (nextBtn) {
			nextBtn.style.opacity = "";
			nextBtn.style.pointerEvents = "";
		}

		if (prevBtn) prevBtn.addEventListener("click", () => this.slider.prev());
		if (nextBtn) nextBtn.addEventListener("click", () => this.slider.next());

		sliderDom.addEventListener("wheel", this._onWheel, { passive: false });
	}

	// Shift+wheel free-scrolls the track with momentum; without shift the
	// event is left alone so the page scrolls normally.
	_onWheel(e) {
		if (!e.shiftKey || !this.slider) return;
		e.preventDefault();

		// Holding shift makes some browsers/trackpads report the gesture on
		// deltaX instead of deltaY (or vice versa) — read whichever moved.
		const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;

		this.velocity += delta * WHEEL_IMPULSE;
		this.velocity = Math.max(
			-MAX_VELOCITY,
			Math.min(MAX_VELOCITY, this.velocity)
		);

		if (!this.rafId) {
			this.rafId = requestAnimationFrame(this._tick);
		}
	}

	_tick() {
		if (!this.slider) return;

		// track.add() expects a value normalized to the slider's own pixel
		// width (the fraction of one full slider-width to move), not a raw
		// pixel amount — same convention Keen Slider's own drag handler uses
		// internally (dividing the pointer's pixel delta by the slider size).
		this.slider.track.add(this.velocity / this.slider.size);

		this.velocity *= FRICTION;

		if (Math.abs(this.velocity) > MIN_VELOCITY) {
			this.rafId = requestAnimationFrame(this._tick);
		} else {
			this.velocity = 0;
			this.rafId = null;
		}
	}

	// Runs continuously for the life of the carousel, independent of what's
	// actually moving it — our own wheel momentum above, Keen Slider's own
	// drag/throw animator, or a moveToIdx() animation from the prev/next
	// buttons. All of those ultimately move the same track.details.position,
	// so measuring its change frame-to-frame gives one tilt source that
	// covers every interaction method uniformly.
	_observeTilt() {
		if (!this.slider) return;

		const position = this.slider.track.details.position;
		const rawVelocity = (position - this.lastPosition) * this.slider.size;
		this.lastPosition = position;

		// A native drag reads position from raw pointermove events, which
		// don't fire at a steady one-per-frame rate — some frames get several,
		// some get none — so the raw per-frame delta above is noisy and
		// makes the tilt jitter. Smoothing it with an exponential moving
		// average evens that out without touching the actual drag/momentum
		// motion, which is unaffected (this only feeds the tilt).
		this.smoothedVelocity +=
			(rawVelocity - this.smoothedVelocity) * TILT_SMOOTHING;
		this._applyTilt(this.smoothedVelocity);

		this.observeRafId = requestAnimationFrame(this._observeTilt);
	}

	// Tilts every card the same amount, in the direction of travel — leans
	// into the turn the way a flicked card would, and eases back to flat as
	// velocity (and therefore tilt) decays to 0 on its own.
	_applyTilt(velocity) {
		const tilt = Math.max(
			-MAX_TILT_DEG,
			Math.min(MAX_TILT_DEG, -velocity * TILT_VELOCITY_FACTOR)
		);
		this.slideInners.forEach((inner) => {
			if (inner) inner.style.transform = `rotateY(${tilt}deg)`;
		});
	}

	destroy() {
		if (this.rafId) cancelAnimationFrame(this.rafId);
		this.rafId = null;
		if (this.observeRafId) cancelAnimationFrame(this.observeRafId);
		this.observeRafId = null;
		if (this.slider) {
			this.slider.destroy();
			this.slider = null;
		}
	}
}

Object.assign(CardCarousel.prototype, event);
export default CardCarousel;
